import { action, autorun, computed, makeObservable } from 'mobx';
import { CurrencySymbolType, moneySymbol } from 'src_common/common/amount/website-money/currency';
import { BetsState } from 'src/domains/sportsbook/betting/state/BetsState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { BetSlipState } from 'src/domains/sportsbook/betting/state/BetSlipState';
import { RabState } from 'src/domains/sportsbook/betting/state/rabState/RabState';
import { MultibetState } from 'src/domains/sportsbook/betting/state/multibetState/MultibetState';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { AlternativeEventState } from 'src/domains/sportsbook/state/alternativeEventState/AlternativeEventState';
import { EventsListState } from 'src/domains/sportsbook/state/eventsListState/EventsListState';
import { UnifiedSportEventsListState } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedSportEventsListState';
import { SpecialSportsState } from 'src/domains/sportsbook/state/specialSportsState/SpecialSportsState';
import { BetWidgetsState } from 'src/domains/sportsbook/state/betWidgetsState/BetWidgetsState';
import { UsersState } from 'src/domains/players/state/UsersState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import {
    RacesSliderState,
    RacesSliderType,
} from 'src/domains/sportsbook/webview/components/racesSlider/RacesSlider.state';
import { HorseRacingState } from 'src/domains/sportsbook/webview/components/racing/NewRacingPage.state';
import { MarketFiltersForSportState } from 'src/domains/sportsbook/state/marketFiltersState/MarketFiltersForSportState';
import { EventsListSelectTabState } from './eventsListState/EventsListSelectTabState';
import { RouteViewType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { CompetitionId, EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { Common } from 'src/domains/common/Common';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { UnifiedEventsListSelectTabState } from './eventsListState/unifiedSportEventsListState/UnifiedEventsListSelectTabState';

export class AppSportsBookState {
    /**
     * @deprecated - please use common.models
     */
    public readonly models: ModelsState;
    public readonly betsState: BetsState;
    /**
     * This method is used by external operators. Do not remove it without informing others.
     */
    public readonly betSlipState: BetSlipState;
    public readonly rab: RabState;
    public readonly multibet: MultibetState;

    public readonly alternativeEventState: MobxMapAutoNew<EventId | null, AlternativeEventState>;
    public readonly eventsListState: MobxMapAutoNew<CompetitionId | null, EventsListState>;

    public readonly unifiedSportEventsSelectTab: MobxMapAutoNew<string, UnifiedEventsListSelectTabState>;
    public readonly unifiedSportEventsListState: MobxMapAutoNew<
        string,
        MobxMapAutoNew<CompetitionId | null, UnifiedSportEventsListState>
    >;
    public readonly specialSportsMap: MobxMapAutoNew<string | null, SpecialSportsState>;
    public readonly betWidgetsState: BetWidgetsState;
    public readonly raceSlider: MobxMapAutoNew<RacesSliderType, RacesSliderState>;

    public readonly horseRacingState: MobxMapAutoNew<'horseracing' | 'greyhoundracing', HorseRacingState>;
    public readonly marketFiltersForSport: MobxMapAutoNew<
        string,
        MobxMapAutoNew<EventId | null, MarketFiltersForSportState>
    >;

    public static get = AutoWeakMap.create((common: Common) => new AppSportsBookState(common));

    private constructor(private readonly common: Common) {
        makeObservable(this);
        const starRouter = StarRouter.get(common);

        this.models = common.models;
        this.betsState = new BetsState(common);
        this.rab = new RabState(() => this.betSlipState.basicBetSlipState.channel, common);
        this.multibet = MultibetState.get(common);
        this.betSlipState = new BetSlipState(this.models.id, this.rab, this.betsState, common);
        this.alternativeEventState = new MobxMapAutoNew((eventId) => {
            return new AlternativeEventState(common, ConfigComponents.get(common), eventId, common.trpcClient);
        });

        const selectTab = new EventsListSelectTabState(common);

        this.eventsListState = new MobxMapAutoNew((competitionId) => {
            return new EventsListState(common, competitionId, selectTab);
        });

        this.unifiedSportEventsSelectTab = new MobxMapAutoNew((sportId) => {
            return new UnifiedEventsListSelectTabState(common, sportId);
        });

        this.unifiedSportEventsListState = new MobxMapAutoNew((sportId) => {
            return new MobxMapAutoNew((competitionId) => {
                return new UnifiedSportEventsListState(
                    common,
                    sportId,
                    competitionId,
                    this.unifiedSportEventsSelectTab.get(sportId)
                );
            });
        });

        this.specialSportsMap = new MobxMapAutoNew((sportId) => {
            return new SpecialSportsState(common, sportId);
        });

        this.betWidgetsState = new BetWidgetsState(this.betSlipState, this.rab, this.multibet);

        this.raceSlider = new MobxMapAutoNew((raceSliderType: RacesSliderType) => {
            return new RacesSliderState(raceSliderType, this.common);
        });

        this.horseRacingState = new MobxMapAutoNew((raceType: 'horseracing' | 'greyhoundracing') => {
            return new HorseRacingState(raceType, this.common, starRouter);
        });

        this.marketFiltersForSport = new MobxMapAutoNew((sport) => {
            return new MobxMapAutoNew((eventId): MarketFiltersForSportState => {
                return new MarketFiltersForSportState(sport, eventId, common.trpcClient, common);
            });
        });

        starRouter.onChangeCurrentView((_prevCurrentView, nextCurrentView) => {
            const eventId = this.getEventId(nextCurrentView);
            const eventModel = eventId === null ? null : this.models.getEvent(eventId);

            if (eventModel !== null) {
                GoogleTagManagerState.get(common).eventViewTag({
                    eventId: eventModel.id,
                    eventName: eventModel.name,
                    eventTime: eventModel.timeSettingsStartTime,
                });
            }
        });

        if (EnvironmentState.get(common).isBrowser) {
            this.runOnStart();
        }
    }

    @action public onAccountGTMRoutingChange = (route: string): void => {
        return GoogleTagManagerState.get(this.common).routingAccountChange(route);
    };

    @computed public get moneySymbol(): CurrencySymbolType {
        return moneySymbol(UsersState.get(this.common).currency);
    }

    public getMarketFiltersBySport = (sport: string, eventId: EventId | null): MarketFiltersForSportState => {
        return this.marketFiltersForSport.get(sport).get(eventId);
    };

    private runOnStart = (): void => {
        //For better UX - preload data for football page - first visit
        // temporary commented to bust performance
        // autorun(() => {
        //     const configItem = this.eventsListState.get(null).marketForEnv[0];

        //     if (configItem !== undefined) {
        //         const collection = this.eventsCollection.listOfSport('football', {
        //             templateId: configItem.template,
        //             line: configItem.line,
        //         });

        //         return collection.length;
        //     }
        // });

        // not sure if it is necessary
        autorun(() => {
            const { legsIds2, referralState } = this.betSlipState;

            legsIds2.map((leg) => {
                const selectionModel = leg.id.getModel();

                if (selectionModel?.display === false && referralState.isReferred === false) {
                    this.betSlipState.legsState.betslipData.onRemoveLeg(leg);
                }
            });
        });

        //For better UX - preload data for football page - change tabs with markets
        // temporary commented to bust performance
        // autorun(() => {
        //     const currentView = this.starRouter.currentView;
        //     const fakeOut: Array<number> = [];

        //     if (currentView?.name === 'sport' && currentView.id === 'football') {
        //         for (const configItem of this.eventsListState.get(null).marketForEnv) {
        //             const collection = this.eventsCollection.listOfSport('football', {
        //                 templateId: configItem.template,
        //                 line: configItem.line,
        //             });

        //             fakeOut.push(collection.length);
        //         }
        //     }
        // });
    };

    private getEventId = (currentView: RouteViewType | null): number | null => {
        if (currentView === null) {
            return null;
        }

        if (currentView.name === 'racecard' && currentView.selected !== null) {
            return currentView.selected;
        }

        if (currentView.name === 'event') {
            return currentView.id;
        }

        return null;
    };
}
